import {
  IOrderVerifyRequestOrderItem,
  ISalePageDetailV2Response,
} from '@/api/types';
import {
  AgeOfUseTermsBottomSheet,
  getRouterPath,
  ProxyGachaSelectBottomSheet,
} from '@/components';
import { useGachaTermsQuery, useIsLoggedIn } from '@/entities';
import {
  useUpsertOrderEntry,
  writeStoredGachaProxyFlag,
} from '@/entities/payment';
import { useProductAdditionalInforms } from '@/entities/salePage';
import { requestV2 } from '@/lib/request';
import webViewService from '@/lib/webViewService';
import { useOverlay } from '@vuddy/components';
import { getClientSideUserAgent } from '@vuddy/utils';
import { find } from 'lodash-es';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface IUseHandleGachaOnSaleItemProps {
  salePage: ISalePageDetailV2Response;
  isPreview?: boolean;
}

export const useHandleGachaOnSaleItem = ({
  salePage,
  isPreview = false,
}: IUseHandleGachaOnSaleItemProps) => {
  const router = useRouter();
  const routerPath = getRouterPath({
    isAppWebView: webViewService.isAppWebView(getClientSideUserAgent()),
  });
  const { openBottomSheet } = useOverlay();
  const { mutateAsync: upsertOrderEntry, isPending: isUpsertingOrderEntry } =
    useUpsertOrderEntry();
  const [showProxyGachaSelectBottomSheet, setShowProxyGachaSelectBottomSheet] =
    useState(false);
  const [productId, setProductId] = useState<number | undefined>();
  const [promotionCode, setPromotionCode] = useState<string | undefined>();

  const { isLoggedIn } = useIsLoggedIn();
  const { data: gachaTerms } = useGachaTermsQuery();
  const {
    data: productAdditionalInforms,
    isSuccess: isSuccessAdditionalInforms,
  } = useProductAdditionalInforms(salePage.id);

  const isStock = salePage.gacha?.logicType === 'STOCK';

  const newProducts = useMemo(
    () =>
      isSuccessAdditionalInforms || !isLoggedIn
        ? (salePage?.products ?? [])
            .map(product => ({
              ...product,
              ...find(
                productAdditionalInforms ?? [],
                purchasableProduct =>
                  purchasableProduct.productId === product.id
              ),
            }))
            .filter(item => {
              // 재고 기반인 경우에는 판매중 필터링 필요 없음
              if (isStock) {
                return isPreview || !isLoggedIn || item.isPurchasable;
              }

              return (
                item.saleStatus === 'ON_SALE' &&
                (isPreview || !isLoggedIn || item.isPurchasable)
              );
            })
        : [],
    [
      isLoggedIn,
      isPreview,
      isStock,
      isSuccessAdditionalInforms,
      productAdditionalInforms,
      salePage?.products,
    ]
  );

  const onClickOnSaleItem = async (
    productId: number,
    promotionCode?: string
  ) => {
    setProductId(productId);
    setPromotionCode(promotionCode);

    if (!gachaTerms?.ageOfUseTerms.agreed) {
      openBottomSheet({
        component: AgeOfUseTermsBottomSheet,
        props: {
          onSuccess: () => setShowProxyGachaSelectBottomSheet(true),
        },
      });
    } else {
      setShowProxyGachaSelectBottomSheet(true);
    }
  };

  const handleGachaRequest = useCallback(
    (isProxy: boolean) => async (onClose?: VoidFunction) => {
      if (isUpsertingOrderEntry) {
        return;
      }

      writeStoredGachaProxyFlag(isProxy);
      const item = newProducts.filter(product => product.id === productId)[0];

      if (!item) {
        onClose?.();
        return router.refresh();
      }

      const orderItems: IOrderVerifyRequestOrderItem[] = [
        {
          referenceId: item.id,
          referenceType: item.type,
          quantity: 1,
          promotionCode,
        },
      ];

      try {
        const { entryId } = await upsertOrderEntry({
          salePageId: salePage.id,
          entrySource: 'sale',
          channelId: salePage.channel.id,
          orderItems,
          isProxy,
        });

        router.push(routerPath.orderSheetByEntry(salePage.id, entryId));
      } catch {}
    },
    [
      isUpsertingOrderEntry,
      newProducts,
      productId,
      promotionCode,
      router,
      routerPath,
      salePage,
      upsertOrderEntry,
    ]
  );

  const requestShowProxyGachaSelectBottomSheet = useCallback(async () => {
    setShowProxyGachaSelectBottomSheet(false);

    const response = await requestV2.get<ISalePageDetailV2Response>(
      `/sale-pages/${salePage.id}/detail`
    );

    const isProxyCompleteAllowed = response.gacha?.isProxyCompleteAllowed;

    if (response?.gacha && isProxyCompleteAllowed) {
      openBottomSheet({
        component: ProxyGachaSelectBottomSheet,
        props: {
          channelName: response.channel.name,
          resultAutoCompleteAt: response.gacha.resultAutoCompleteAt,
          handleProxyGachaRequest: handleGachaRequest(true),
          handleGachaRequest: handleGachaRequest(false),
        },
      });
    } else {
      handleGachaRequest(false)();
    }
  }, [handleGachaRequest, openBottomSheet, salePage.id]);

  useEffect(() => {
    if (showProxyGachaSelectBottomSheet) {
      requestShowProxyGachaSelectBottomSheet();
    }
  }, [requestShowProxyGachaSelectBottomSheet, showProxyGachaSelectBottomSheet]);

  return {
    onClickOnSaleItem,
    products: newProducts,
  };
};
